import React, { useEffect, useState } from "react";
import PublicHeader from "../components/PublicHeader";
import PublicFooter from "../components/PublicFooter";
import SeoHead from "../components/SeoHead";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Check, Copy, Tag, Calendar } from "lucide-react";

function formatDDMM(iso) {
  if (!iso) return "";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch {
    return iso;
  }
}

function CouponCard({ c, lang }) {
  const [copied, setCopied] = useState(false);
  const title = (lang === "en" ? c.title_en : c.title_ro) || (lang === "en" ? "Special offer" : "Oferta speciala");
  const desc = (lang === "en" ? c.description_en : c.description_ro) || "";
  const discount =
    c.discount_pct != null && c.discount_pct > 0
      ? `-${c.discount_pct}%`
      : c.discount_amount_eur != null && c.discount_amount_eur > 0
      ? `-€${c.discount_amount_eur}`
      : "—";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="card-surface p-7 sm:p-8 relative overflow-hidden flex flex-col gap-5"
      data-testid={`coupon-card-${c.code}`}
    >
      {/* Big discount badge */}
      <div
        className="absolute -top-1 -right-1 px-4 py-2 font-serif text-2xl tracking-tight"
        style={{
          background: "var(--brand-primary)",
          color: "#0A0A0A",
          clipPath: "polygon(15% 0, 100% 0, 100% 100%, 0% 100%)",
        }}
      >
        {discount}
      </div>

      <div className="flex items-center gap-2 label-eyebrow" style={{ color: "var(--brand-primary)" }}>
        <Tag className="w-3.5 h-3.5" />
        {lang === "en" ? "Coupon code" : "Cod cupon"}
      </div>

      <h3 className="font-serif text-2xl pr-16">{title}</h3>

      {desc && (
        <p className="text-sm leading-relaxed" style={{ color: "var(--brand-text-secondary)" }}>
          {desc}
        </p>
      )}

      <div
        className="flex items-center justify-between gap-3 border-2 border-dashed px-4 py-3 mt-auto"
        style={{ borderColor: "var(--brand-primary)" }}
      >
        <code
          className="text-lg font-mono tracking-widest font-bold"
          style={{ color: "var(--brand-primary)" }}
          data-testid={`coupon-code-${c.code}`}
        >
          {c.code}
        </code>
        <button
          onClick={copy}
          className="btn-ghost flex items-center gap-2 text-xs uppercase tracking-widest"
          data-testid={`coupon-copy-${c.code}`}
        >
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5" /> {lang === "en" ? "Copied" : "Copiat"}
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5" /> {lang === "en" ? "Copy" : "Copiaza"}
            </>
          )}
        </button>
      </div>

      {c.valid_to && (
        <div
          className="flex items-center gap-2 text-xs"
          style={{ color: "var(--brand-text-secondary)" }}
        >
          <Calendar className="w-3 h-3" />
          {lang === "en" ? "Valid until" : "Valabil pana la"} <b>{formatDDMM(c.valid_to)}</b>
        </div>
      )}
    </div>
  );
}

export default function Offers() {
  const { lang } = useI18n();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/coupons/public")
      .then((r) => setCoupons(Array.isArray(r.data) ? r.data : []))
      .catch(() => setCoupons([]))
      .finally(() => setLoading(false));
  }, []);

  const title = lang === "en" ? "Special Offers" : "Oferte Speciale";
  const subtitle =
    lang === "en"
      ? "Active coupon codes. Copy & paste at checkout."
      : "Coduri de cupoane active. Copiaza si foloseste-le la rezervare.";

  return (
    <div>
      <SeoHead title={title} description={subtitle} />
      <PublicHeader />

      <section className="pt-32 pb-20 px-6 md:px-12 max-w-[1440px] mx-auto min-h-screen" data-testid="offers-page">
        <div className="mb-12 sm:mb-16">
          <div className="label-eyebrow mb-4" style={{ color: "var(--brand-primary)" }}>
            {lang === "en" ? "Promotions" : "Promotii"}
          </div>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4">
            {title}
          </h1>
          <p className="text-sm sm:text-base max-w-2xl" style={{ color: "var(--brand-text-secondary)" }}>
            {subtitle}
          </p>
        </div>

        {loading ? (
          <div className="text-center py-20 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
            {lang === "en" ? "Loading..." : "Se incarca..."}
          </div>
        ) : coupons.length === 0 ? (
          <div className="text-center py-20" data-testid="offers-empty">
            <Tag className="w-12 h-12 mx-auto mb-4 opacity-40" />
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>
              {lang === "en"
                ? "No active offers right now. Check back soon!"
                : "Nu sunt oferte active acum. Revino curand!"}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {coupons.map((c) => (
              <CouponCard key={c.code} c={c} lang={lang} />
            ))}
          </div>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
